/**
 * Quotes Slider functionality
 */

interface QuotesSliderOptions {
  autoplay: boolean;
  autoplayDelay: number;
}

class QuotesSlider {
  private container: HTMLElement;
  private track: HTMLElement;
  private slides: NodeListOf<HTMLElement>;
  private prevButton: HTMLElement | null;
  private nextButton: HTMLElement | null;
  private dots: NodeListOf<HTMLElement>;
  private currentIndex: number = 0;
  private slideWidth: number = 0;
  private gap: number = 20;
  private positions: number[] = [];
  private originalTotal: number = 0;
  private autoplayInterval: number | null = null;
  private options: QuotesSliderOptions;

  constructor(container: HTMLElement) {
    this.container = container;
    this.track = this.container.querySelector(
      '.quotes-slider__track'
    ) as HTMLElement;
    this.slides = this.container.querySelectorAll('.quotes-slider__slide');
    this.prevButton = this.container.querySelector('.quotes-slider__nav--prev');
    this.nextButton = this.container.querySelector('.quotes-slider__nav--next');
    this.dots = this.container.querySelectorAll('.quotes-slider__dot');

    // Get options from data attributes
    this.options = {
      autoplay: this.container.dataset.autoplay === 'true',
      autoplayDelay: parseInt(
        this.container.dataset.autoplayDelay || '5000',
        10
      ),
    };

    this.init();
  }

  private init(): void {
    if (this.slides.length === 0) return;

    // Clone slides for infinite loop effect
    this.cloneSlidesForInfiniteLoop();

    // Set initial position to first real slide (after clones)
    this.originalTotal = Math.floor(this.slides.length / 3); // Since we have 3 sets
    this.currentIndex = this.originalTotal;

    this.calculateDimensions();
    this.attachEventListeners();
    this.updateSlidePosition(false); // No animation for initial

    if (this.options.autoplay) {
      this.startAutoplay();
    }

    // Pause autoplay on hover
    this.container.addEventListener('mouseenter', () => this.stopAutoplay());
    this.container.addEventListener('mouseleave', () => {
      if (this.options.autoplay) {
        this.startAutoplay();
      }
    });
  }

  private calculateDimensions(): void {
    const firstSlide = this.slides[0];
    if (!firstSlide) return;

    // Calculate actual slide width including margins/padding
    this.slideWidth = firstSlide.offsetWidth;

    // Get gap from computed style
    const trackStyle = window.getComputedStyle(this.track);
    const gapValue = trackStyle.gap || trackStyle.columnGap || '20px';
    this.gap = parseFloat(gapValue);

    // Calculate positions theoretically since slides are uniform
    this.positions = [];
    const totalSlides = this.slides.length;
    for (let i = 0; i < totalSlides; i++) {
      this.positions.push(i * (this.slideWidth + this.gap));
    }
  }

  private isMobile(): boolean {
    return window.innerWidth < 768;
  }

  private cloneSlidesForInfiniteLoop(): void {
    // Clone all slides and append/prepend for seamless infinite loop
    const slidesArray = Array.from(this.slides);
    const beforeClones: HTMLElement[] = [];

    slidesArray.forEach(slide => {
      const cloneBefore = slide.cloneNode(true) as HTMLElement;
      const cloneAfter = slide.cloneNode(true) as HTMLElement;
      cloneBefore.classList.add('is-clone');
      cloneAfter.classList.add('is-clone');
      beforeClones.push(cloneBefore);
      this.track.appendChild(cloneAfter);
    });

    // Insert before clones in correct order (reverse the array to maintain order)
    beforeClones.reverse().forEach(clone => {
      this.track.insertBefore(clone, this.track.firstChild);
    });

    // Update slides NodeList to include clones
    this.slides = this.container.querySelectorAll('.quotes-slider__slide');
  }

  private attachEventListeners(): void {
    // Navigation buttons
    this.prevButton?.addEventListener('click', () => this.prevSlide());
    this.nextButton?.addEventListener('click', () => this.nextSlide());

    // Pagination dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Recalculate on window resize
    window.addEventListener('resize', () => {
      this.calculateDimensions();
      this.updateSlidePosition();
    });

    // Keyboard navigation
    this.container.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });
  }

  private updateSlidePosition(animate: boolean = true): void {
    let offset = -this.positions[this.currentIndex];

    if (this.isMobile()) {
      const containerWidth = this.container.offsetWidth;
      offset += containerWidth / 2 - this.slideWidth / 2;
    }

    if (animate) {
      this.track.style.transition =
        'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    } else {
      this.track.style.transition = 'none';
    }

    this.track.style.transform = `translateX(${offset}px)`;
    this.updateDots();
  }

  private updateDots(): void {
    // Calculate the actual slide index (accounting for clones)
    let actualIndex = this.currentIndex % this.originalTotal;
    if (actualIndex < 0) actualIndex += this.originalTotal;

    this.dots.forEach((dot, index) => {
      const isActive = index === actualIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive.toString());
    });
  }

  private nextSlide(): void {
    this.currentIndex++;
    this.updateSlidePosition();

    // Check if we've reached a cloned slide at the end
    setTimeout(() => {
      if (this.currentIndex >= this.originalTotal * 2) {
        this.currentIndex -= this.originalTotal;
        this.updateSlidePosition(false);
      }
    }, 500);
  }

  private prevSlide(): void {
    this.currentIndex--;
    this.updateSlidePosition();

    // Check if we've reached a cloned slide at the beginning
    setTimeout(() => {
      if (this.currentIndex < this.originalTotal) {
        this.currentIndex += this.originalTotal;
        this.updateSlidePosition(false);
      }
    }, 500);
  }

  private goToSlide(index: number): void {
    this.currentIndex = this.originalTotal + index;
    this.updateSlidePosition();
    this.resetAutoplay();
  }

  private startAutoplay(): void {
    if (this.autoplayInterval) return;

    this.autoplayInterval = window.setInterval(() => {
      this.nextSlide();
    }, this.options.autoplayDelay);
  }

  private stopAutoplay(): void {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  private resetAutoplay(): void {
    if (this.options.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }
}

// Initialize all quotes sliders on the page
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll<HTMLElement>('.quotes-slider');
  sliders.forEach(slider => new QuotesSlider(slider));
});

// Also initialize when Gutenberg re-renders blocks in the editor
declare global {
  interface Window {
    acf?: {
      addAction: (action: string, callback: () => void) => void;
    };
  }
}

if (window.acf) {
  window.acf.addAction('render_block_preview/type=acf/quotes-slider', () => {
    const sliders = document.querySelectorAll<HTMLElement>('.quotes-slider');
    sliders.forEach(slider => new QuotesSlider(slider));
  });
}

export default QuotesSlider;
